import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ShoppingBag, User, Menu, X, Sun, Moon, Shield, Bell, Heart, Search, ChevronRight, LogIn } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import LanguageSwitcher from './LanguageSwitcher';
import SearchPalette from './SearchPalette';

const Header = () => {
  const { user, api } = useAuth();
  const { cart } = useCart();
  const { theme, toggleTheme } = useTheme();
  const { currency, setCurrency, SUPPORTED_CURRENCIES } = useCurrency();
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const { lang } = useParams();
  const prefix = `/${lang || 'en'}`;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(o => !o);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get('/api/user/notifications?unread_only=true')
      .then(r => setUnreadCount(r.data.unread_count))
      .catch(() => {});
  }, [user, location]);

  // Lock body scroll when mobile menu open — iOS-safe
  useEffect(() => {
    if (mobileOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollY) window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollY) window.scrollTo(0, parseInt(scrollY || '0') * -1);
    };
  }, [mobileOpen]);

  const mainNavLinks = [
    { to: `${prefix}/collections/straight`, label: t('nav.straight') },
    { to: `${prefix}/collections/wavy`, label: t('nav.wavy') },
    { to: `${prefix}/collections/curly`, label: t('nav.curly') },
    { to: `${prefix}/shop-by-shade`, label: t('shade.title') },
  ];

  const allNavLinks = [...mainNavLinks];

  const cycleCurrency = () => {
    const idx = SUPPORTED_CURRENCIES.indexOf(currency);
    setCurrency(SUPPORTED_CURRENCIES[(idx + 1) % SUPPORTED_CURRENCIES.length]);
  };

  return (
    <>
    <header
      className={`fixed top-[36px] left-0 right-0 z-50 transition-all duration-700 ${
        scrolled ? 'glass-header border-b' : 'bg-transparent'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-28">
          {/* Logo */}
          <Link to={prefix} className="flex-shrink-0 transition-transform duration-300 hover:scale-105" data-testid="logo-link">
            <img src="/zelina-logo.webp" alt="Zelina Studios" className="h-10 md:h-20 lg:h-24 w-auto object-contain" decoding="async" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8 lg:gap-10">
            {mainNavLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="nav-link text-base font-medium transition-colors duration-300 py-1 hover:text-[var(--gold)]"
                style={{ color: location.pathname.startsWith(link.to) ? 'var(--gold)' : 'var(--text-secondary)' }}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Right Actions */}
          <div className="hidden md:flex items-center gap-4 lg:gap-5">
            <LanguageSwitcher />

            <button onClick={cycleCurrency}
              className="text-xs font-semibold tracking-wider px-2 py-1 rounded border border-[var(--text-primary)]/10 hover:border-[var(--gold)]/40 transition-all duration-300"
              style={{ color: 'var(--text-secondary)' }}
              data-testid="currency-toggle" title="Switch currency" aria-label={`Currency: ${currency}. Click to switch`}>
              {currency}
            </button>

            <button onClick={() => setSearchOpen(true)}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:bg-[var(--gold-muted)] hover:scale-110"
              data-testid="search-trigger" title="Search" aria-label="Search products">
              <Search className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
            </button>

            <button onClick={toggleTheme}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:bg-[var(--gold-muted)] hover:scale-110"
              data-testid="theme-toggle" aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
              {theme === 'dark'
                ? <Sun className="w-[18px] h-[18px]" style={{ color: 'var(--text-secondary)' }} strokeWidth={1.5} />
                : <Moon className="w-[18px] h-[18px]" style={{ color: 'var(--text-secondary)' }} strokeWidth={1.5} />
              }
            </button>

            <Link to={`${prefix}/cart`} className="relative group" data-testid="cart-link" aria-label="Shopping bag">
              <ShoppingBag className="w-5 h-5 transition-all duration-300 group-hover:scale-110" style={{ color: 'var(--text-secondary)' }} strokeWidth={1.5} />
              {cart.items.length > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: 'var(--gold)', color: '#0A0A0A' }} data-testid="cart-count">
                  {cart.items.length}
                </span>
              )}
            </Link>

            {user && (user.role === 'superadmin' || user.role === 'admin') && (
              <Link to="/admin" data-testid="admin-dashboard-link" className="transition-transform duration-300 hover:scale-110" title="Admin Dashboard">
                <Shield className="w-5 h-5" style={{ color: 'var(--gold)' }} strokeWidth={1.5} />
              </Link>
            )}

            {user && (
              <>
                <Link to={`${prefix}/account?tab=wishlist`} data-testid="wishlist-header-link" className="transition-transform duration-300 hover:scale-110" title="Wishlist">
                  <Heart className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} strokeWidth={1.5} />
                </Link>
                <Link to={`${prefix}/notifications`} className="relative transition-transform duration-300 hover:scale-110" data-testid="notifications-link" title="Notifications">
                  <Bell className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} strokeWidth={1.5} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center bg-red-500 text-white" data-testid="notification-count">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
              </>
            )}

            {user ? (
              <Link to={`${prefix}/account`} data-testid="account-link" className="transition-transform duration-300 hover:scale-110">
                <User className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} strokeWidth={1.5} />
              </Link>
            ) : (
              <Link to={`${prefix}/login`} className="nav-link text-base font-semibold transition-colors duration-300 py-1" style={{ color: 'var(--gold)' }} data-testid="login-link">
                {t('nav.sign_in')}
              </Link>
            )}
          </div>

          {/* Mobile Right Actions: Lang, Currency, Search, Cart + Hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <button onClick={cycleCurrency}
              className="text-[10px] font-semibold tracking-wider px-2 py-1 rounded border border-[var(--text-primary)]/10"
              style={{ color: 'var(--text-secondary)' }}
              data-testid="mobile-header-currency">
              {currency}
            </button>
            <button onClick={() => setSearchOpen(true)}
              className="w-9 h-9 rounded-full flex items-center justify-center"
              data-testid="mobile-search-trigger" aria-label="Search products">
              <Search className="w-[18px] h-[18px]" style={{ color: 'var(--text-secondary)' }} />
            </button>

            <Link to={`${prefix}/cart`} className="relative" data-testid="mobile-cart-link" aria-label="Shopping bag">
              <ShoppingBag className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} strokeWidth={1.5} />
              {cart.items.length > 0 && (
                <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center" style={{ background: 'var(--gold)', color: '#0A0A0A' }} data-testid="mobile-cart-count">
                  {cart.items.length}
                </span>
              )}
            </Link>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              style={{ color: 'var(--text-secondary)' }}
              data-testid="mobile-menu-toggle"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile Drawer ── */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-[100px] z-[60] overflow-y-auto animate-slideIn" style={{ backgroundColor: '#0A0A0A' }} data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-1">
            {/* Navigation Links */}
            {mainNavLinks.map((link) => (
              <Link key={link.to} to={link.to}
                className="flex items-center justify-between py-3.5 text-lg font-medium border-b border-[var(--text-primary)]/5 transition-colors"
                style={{ color: 'var(--text-secondary)' }}>
                {link.label}
                <ChevronRight className="w-4 h-4 opacity-30" />
              </Link>
            ))}

            {/* Divider */}
            <div className="pt-4 pb-2">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: 'var(--text-muted)' }}>Account</p>
            </div>

            {user ? (
              <>
                <Link to={`${prefix}/account`}
                  className="flex items-center gap-3 py-3 text-base font-medium transition-colors"
                  style={{ color: 'var(--text-secondary)' }}>
                  <User className="w-5 h-5" /> My Account
                </Link>
                <Link to={`${prefix}/account?tab=wishlist`}
                  className="flex items-center gap-3 py-3 text-base font-medium transition-colors"
                  style={{ color: 'var(--text-secondary)' }}>
                  <Heart className="w-5 h-5" /> Wishlist
                </Link>
                <Link to={`${prefix}/notifications`}
                  className="flex items-center gap-3 py-3 text-base font-medium transition-colors"
                  style={{ color: 'var(--text-secondary)' }}>
                  <Bell className="w-5 h-5" /> Notifications
                  {unreadCount > 0 && <span className="ml-auto w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center bg-red-500 text-white">{unreadCount}</span>}
                </Link>
                {(user.role === 'superadmin' || user.role === 'admin') && (
                  <Link to="/admin"
                    className="flex items-center gap-3 py-3 text-base font-medium transition-colors"
                    style={{ color: 'var(--gold)' }} data-testid="mobile-admin-link">
                    <Shield className="w-5 h-5" /> Admin Dashboard
                  </Link>
                )}
              </>
            ) : (
              <Link to={`${prefix}/login`}
                className="flex items-center gap-3 py-3 text-base font-semibold transition-colors"
                style={{ color: 'var(--gold)' }}
                data-testid="mobile-login-link">
                <LogIn className="w-5 h-5" /> {t('nav.sign_in')}
              </Link>
            )}

            {/* Settings */}
            <div className="pt-4 pb-2">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: 'var(--text-muted)' }}>Settings</p>
            </div>

            <div className="flex items-center justify-between py-3">
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Currency</span>
              <button onClick={cycleCurrency}
                className="text-xs font-semibold tracking-wider px-3 py-1.5 rounded border border-[var(--text-primary)]/10"
                style={{ color: 'var(--text-secondary)' }}
                data-testid="mobile-currency-toggle">
                {currency}
              </button>
            </div>

            <div className="flex items-center justify-between py-3">
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Theme</span>
              <button onClick={toggleTheme}
                className="w-9 h-9 rounded-full flex items-center justify-center border border-[var(--text-primary)]/10">
                {theme === 'dark'
                  ? <Sun className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                  : <Moon className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                }
              </button>
            </div>

            <div className="flex items-center justify-between py-3">
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Language</span>
              <LanguageSwitcher mobile />
            </div>
          </div>
        </div>
      )}
    </header>
    <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default Header;
